import { useMemo } from 'react';

import { getSymbolsByDataRef } from '@flipster/features/src/shared/market-layout';
import { getQuoteCurrencyPairSymbols } from '@flipster/features/src/shared/quote-currency';
import { TradFiCategory, getTradFiCategorySymbols } from '@flipster/features/src/shared/rwa';
import { useSymbols } from '@flipster/features/src/shared/symbol/use-symbols';
import { QuoteCurrencyListExceptUsdt } from '@flipster/utils/constants/currency';
import { mapToObj } from '@flipster/utils/map-to-obj';

import { MarketCarouselType } from './constants';

const topCount = 3;

export function usePerpTopMarkets() {
  const { symbols: symbolsResponse } = useSymbols('perpetual');
  const data = symbolsResponse?.data;

  const tradfiSectionItems = useMemo(() => {
    const byCategory = (category: TradFiCategory) =>
      getTradFiCategorySymbols(symbolsResponse, category).slice(0, topCount);
    return {
      tradfiAll: byCategory('all'),
      tradfiPreIpo: byCategory('preIpo'),
      tradfiStocks: byCategory('stock'),
      tradfiEtf: byCategory('etf'),
      tradfiCommodity: byCategory('commodity'),
    };
  }, [symbolsResponse]);

  const quoteCurrencyPairs = useMemo(
    () =>
      mapToObj(QuoteCurrencyListExceptUsdt, (currency) =>
        getQuoteCurrencyPairSymbols(data, currency).slice(0, topCount),
      ),
    [data],
  );

  const sortedTopMarkets: Record<MarketCarouselType, string[] | undefined> = useMemo(() => {
    const top = (dataRef: string) => getSymbolsByDataRef(data, dataRef).slice(0, topCount);
    return {
      tradfi: tradfiSectionItems.tradfiAll,
      hotTradings: top('hotTradings'),
      newListings: top('newListings'),
      priceChange24h: top('priceChange24h'),
      volume: top('volume'),
      absFundingRates: top('absFundingRates'),
      yourFrequentTrades: top('yourFrequentTrades'),
      ...tradfiSectionItems,
      ...quoteCurrencyPairs,
    };
  }, [data, tradfiSectionItems, quoteCurrencyPairs]);

  return { sortedTopMarkets };
}
